import { Channel, Movie, Series } from '../types/iptv';
import { resolveCorrectChannelLogo } from './channelLogos';
import { generateDynamicPoster } from './imageUtils';
import { extractChannelQualityInfo } from './channelQualityUtils';
import { providerTimestampToMs } from './continueWatching';

export interface ParseResult {
  channels: Channel[];
  movies: Movie[];
  series: Series[];
  errors: string[];
}

/**
 * Fast single-pass parser for EXTINF lines.
 * Extracts tvg-id, tvg-name, tvg-logo, group-title and channel name in one single scan.
 */
function parseExtInfFast(line: string) {
  let tvgId = '';
  let tvgName = '';
  let tvgLogo = '';
  let groupTitle = '';

  const attrRegex = /([a-zA-Z0-9_-]+)=(?:["']([^"']*)["']|([^"\s,]+))/g;
  let match;
  while ((match = attrRegex.exec(line)) !== null) {
    const key = match[1].toLowerCase();
    const val = match[2] !== undefined ? match[2] : match[3];
    if (key === 'tvg-id' || key === 'id') tvgId = val;
    else if (key === 'tvg-name' || key === 'name') tvgName = val;
    else if (key === 'tvg-logo' || key === 'logo') tvgLogo = val;
    else if (key === 'group-title' || key === 'group') groupTitle = val;
  }

  const commaIndex = line.lastIndexOf(',');
  let name = commaIndex !== -1 ? line.substring(commaIndex + 1).trim() : '';
  if (!name && tvgName) {
    name = tvgName;
  }

  return { tvgId, tvgName, tvgLogo, groupTitle, name };
}

/**
 * Intelligent Series, Season & Episode extractor for IPTV playlists.
 * Extracts clean series title, season number and episode number from various naming conventions.
 */
export function extractSeriesEpisodeInfo(rawName: string) {
  let seasonNumber = 1;
  let episodeNumber = 1;

  // Patterns for Season & Episode:
  // 1. S01E02, T01E02, S1E2, T1E2, S01.E02, S01_E02, S01 E02, T01 E02, S01EP02, T01EP02, S01-E02
  const sMatch = rawName.match(/(?:[\[\(\s\-_]|^)(?:s|t)(\d{1,2})[\s\.\-_]*(?:e|ep)(\d{1,3})(?:[\]\)\s\-_]|$)/i);
  // 2. 1x02 or 01x02 or 1X02
  const xMatch = rawName.match(/(?:[\[\(\s\-_]|^)(\d{1,2})\s*[xX]\s*(\d{1,3})(?:[\]\)\s\-_]|$)/);
  // 3. 1ª Temporada - Ep 02 / 1a Temp Ep 02 / 1º Temp Ep 02 / 1ª Temp / 1 Temporada
  const ordTempMatch = rawName.match(/(\d{1,2})\s*[ªaºo°]?\s*(?:temporada|temp\.?|season|parte|part|t\.?)[^\d]*(?:(\d{1,3})\s*[ªaºo°]?\s*(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)|(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)\s*(\d{1,3})|(\d{1,3}))/i);
  // 4. Temporada 1 Episodio 2 / Season 1 Episode 2 / Temp 1 Ep 2 / T1 Ep 2 / Parte 1 Ep 2
  const tempEpMatch = rawName.match(/(?:temporada|season|temp\.?|parte|part|t\.?)\s*(\d{1,2})[^\d]*(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?|e)\s*(\d{1,3})/i);
  // 5. Cap. 01 / Capitulo 01 / Ep. 01 / 01º Ep / E01 / EP01
  const capMatch = rawName.match(/(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?|e)\s*(\d{1,3})|(\d{1,3})\s*[ªaºo°]?\s*(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)/i);
  // 6. Just S01 or T01 or Temp 1 at the end / in tags
  const shortSeasonMatch = rawName.match(/(?:[\[\(\s\-_]|^)(?:s|t|temp\.?|temporada|parte|part)\s*(\d{1,2})(?:[\]\)\s\-_]*$|[\]\)])/i);

  if (sMatch) {
    seasonNumber = parseInt(sMatch[1], 10);
    episodeNumber = parseInt(sMatch[2], 10);
  } else if (xMatch) {
    seasonNumber = parseInt(xMatch[1], 10);
    episodeNumber = parseInt(xMatch[2], 10);
  } else if (ordTempMatch) {
    seasonNumber = parseInt(ordTempMatch[1], 10);
    episodeNumber = parseInt(ordTempMatch[2] || ordTempMatch[3] || ordTempMatch[4] || '1', 10);
  } else if (tempEpMatch) {
    seasonNumber = parseInt(tempEpMatch[1], 10);
    episodeNumber = parseInt(tempEpMatch[2], 10);
  } else if (capMatch) {
    episodeNumber = parseInt(capMatch[1] || capMatch[2], 10);
    const seasonSolo = rawName.match(/(?:(\d{1,2})\s*[ªaºo°]?\s*(?:temporada|temp\.?|season|parte|part)|(?:temporada|season|temp\.?|parte|part)\s*(\d{1,2}))/i);
    if (seasonSolo) {
      seasonNumber = parseInt(seasonSolo[1] || seasonSolo[2], 10);
    }
  } else if (shortSeasonMatch) {
    seasonNumber = parseInt(shortSeasonMatch[1], 10);
  } else {
    const seasonSolo = rawName.match(/(?:(\d{1,2})\s*[ªaºo°]?\s*(?:temporada|temp\.?|season|parte|part)|(?:temporada|season|temp\.?|parte|part)\s*(\d{1,2}))/i);
    const epSolo = rawName.match(/(?:(\d{1,3})\s*[ªaºo°]?\s*(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)|(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)\s*(\d{1,3}))/i);
    if (seasonSolo) seasonNumber = parseInt(seasonSolo[1] || seasonSolo[2], 10);
    if (epSolo) episodeNumber = parseInt(epSolo[1] || epSolo[2], 10);
  }

  // Extract clean series title (remove prefix tags, season/episode tags, quality tags, dub tags)
  let cleanTitle = rawName
    // Remove group prefixes
    .replace(/^\[(?:SÉRIES|SERIES|VOD|VOD SÉRIES|NOVELAS|ANIMES|DORAMAS)\]\s*/i, '')
    .replace(/^(?:SÉRIES|SERIES|VOD SÉRIES|VOD|NOVELAS|ANIMES|DORAMAS)\s*[\-:|]\s*/i, '')
    // Remove quality and dub tags in brackets/parentheses
    .replace(/\[(?:FHD|HD|SD|4K|UHD|1080p|720p|DUBLADO|DUB|LEG|LEGENDADO|MULTI|HDR|HEVC|COMPLETO)\]/gi, '')
    .replace(/\((?:FHD|HD|SD|4K|UHD|1080p|720p|DUBLADO|DUB|LEG|LEGENDADO|MULTI|HDR|HEVC|COMPLETO)\)/gi, '')
    // Remove year tags like (2021) or [2021]
    .replace(/[\(\[\{]\s*(?:19|20)\d{2}\s*[\)\]\}]/g, '')
    // Remove S01E01 / T01EP01 and everything after
    .replace(/(?:[\[\(\s\-_]|^)(?:s|t)\d{1,2}[\s\.\-_]*(?:e|ep)\d{1,3}(?:[\]\)\s\-_]|$).*/i, '')
    // Remove 1x01 and everything after
    .replace(/(?:[\[\(\s\-_]|^)\d{1,2}\s*[xX]\s*\d{1,3}(?:[\]\)\s\-_]|$).*/i, '')
    // Remove 1ª Temporada / 1a Temp / Temporada 1 / Parte 1 and everything after
    .replace(/(?:\d{1,2}\s*[ªaºo°]?\s*(?:temporada|temp\.?|season|parte|part)|(?:temporada|season|temp\.?|parte|part)\s*\d{1,2}).*/i, '')
    // Remove standalone [T01], (T01), [S01], (S01), [Temp 1], etc.
    .replace(/[\(\[]\s*(?:s|t|temp\.?|temporada)\s*\d{1,2}\s*[\)\]]/gi, '')
    // Remove Episodio 01 / Ep 01 and everything after
    .replace(/(?:\d{1,3}\s*[ªaºo°]?\s*(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)|(?:epis[oó]dio|cap[ií]tulo|cap\.?|ep\.?)\s*\d{1,3}).*/i, '')
    // Remove plain word tags
    .replace(/\b(?:DUBLADO|LEGENDADO|DUB|LEG|FHD|4K|1080P|720P)\b/gi, '')
    // Remove standalone season markers at the end (e.g. S01, T1, - 01)
    .replace(/(?:[\[\(\s\-_]|^)(?:s|t)\d{1,2}(?:[\]\)\s\-_]*$)/i, '')
    .replace(/(?:\s+|-|_|#)0*\d{1,2}$/, '');

  if (!cleanTitle) cleanTitle = rawName.trim();

  return {
    cleanSeriesTitle: cleanTitle,
    seasonNumber: Math.max(1, seasonNumber || 1),
    episodeNumber: Math.max(1, episodeNumber || 1),
    episodeTitle: rawName,
  };
}

export function extractReleaseYear(title?: string, ...candidates: (string | number | undefined)[]): number {
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== '') {
      const s = String(c).trim();
      const match = s.match(/\b(19\d{2}|20[0-2]\d)\b/);
      if (match) {
        const y = parseInt(match[1], 10);
        if (y >= 1920 && y <= 2026) return y;
      }
    }
  }
  if (title) {
    const matchExplicit = title.match(/[\(\[\-\|\s_](19\d{2}|20[0-2]\d)[\)\]\-\|\s_$]/);
    if (matchExplicit) {
      const y = parseInt(matchExplicit[1], 10);
      if (y >= 1920 && y <= 2026) return y;
    }
    const matchAny = title.match(/\b(19\d{2}|20[0-2]\d)\b/);
    if (matchAny) {
      const y = parseInt(matchAny[1], 10);
      if (y >= 1920 && y <= 2026) return y;
    }
  }
  return 0;
}

export async function parseM3U(m3uContent: string): Promise<ParseResult> {
  const channels: Channel[] = [];
  const movies: Movie[] = [];
  const seriesMap = new Map<string, Series>();
  const errors: string[] = [];

  // Remove BOM if present
  const cleanContent = m3uContent.replace(/^\uFEFF/, '');
  const lines = cleanContent.split(/\r?\n/);

  let currentMetadata: {
    name?: string;
    logo?: string;
    groupTitle?: string;
    tvgId?: string;
    resolution?: '4K' | 'FHD' | 'HD' | 'SD';
  } | null = null;

  let channelIndex = 1;

  const defaultMoviePoster = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500&auto=format&fit=crop&q=80';
  const defaultMovieBackdrop = 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1200&auto=format&fit=crop&q=80';
  const defaultChannelLogo = 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=128&auto=format&fit=crop&q=80';

  for (let i = 0; i < lines.length; i++) {
    // Cede o controle pra thread principal a cada ~3000 linhas processadas, pra não travar a
    // interface durante o carregamento de listas grandes (20k+ filmes / 5k+ séries / 3k+ canais)
    // — especialmente importante em aparelhos com processador mais fraco, como muitos tablets
    // Android. Não muda nenhuma lógica de parsing, só intercala pausas curtas.
    if (i > 0 && i % 3000 === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    const line = lines[i].trim();
    if (!line) continue;

    if (line.startsWith('#EXTINF:')) {
      const parsed = parseExtInfFast(line);
      let name = parsed.name || `Canal ${channelIndex}`;
      let group = parsed.groupTitle ? parsed.groupTitle.trim() : 'Geral';
      if (!group) group = 'Geral';

      const { detectedResolution } = extractChannelQualityInfo(name, group);
      const resolution: '4K' | 'FHD' | 'HD' | 'SD' = detectedResolution || 'HD';

      currentMetadata = {
        name,
        logo: parsed.tvgLogo ? parsed.tvgLogo.trim() : '',
        groupTitle: group,
        tvgId: parsed.tvgId ? parsed.tvgId.trim() : `ch-${channelIndex}`,
        resolution,
      };
    } else if (line.startsWith('#EXTGRP:')) {
      const grp = line.replace('#EXTGRP:', '').trim();
      if (grp && currentMetadata) {
        currentMetadata.groupTitle = grp;
      }
    } else if (
      line.startsWith('http://') ||
      line.startsWith('https://') ||
      line.startsWith('rtmp://') ||
      line.startsWith('p2p://') ||
      line.startsWith('magnet:') ||
      line.startsWith('udp://') ||
      line.startsWith('rtsp://')
    ) {
      if (currentMetadata) {
        const streamUrl = line;
        const lowerUrl = streamUrl.toLowerCase();
        const rawGroup = currentMetadata.groupTitle || 'Geral';
        const lowerGroup = rawGroup.toLowerCase();
        const rawName = currentMetadata.name || '';
        const lowerName = rawName.toLowerCase();

        const isAdult =
          lowerGroup.includes('adult') ||
          lowerGroup.includes('18+') ||
          lowerGroup.includes('xxx') ||
          lowerGroup.includes('erot') ||
          lowerName.includes('xxx') ||
          lowerName.includes('playboy');

        const isVodOrSeries =
          lowerGroup.includes('filme') ||
          lowerGroup.includes('filmes') ||
          lowerGroup.includes('movie') ||
          lowerGroup.includes('movies') ||
          lowerGroup.includes('serie') ||
          lowerGroup.includes('série') ||
          lowerGroup.includes('series') ||
          lowerGroup.includes('séries') ||
          lowerGroup.includes('vod') ||
          lowerGroup.includes('novela') ||
          lowerGroup.includes('anime') ||
          lowerGroup.includes('dorama') ||
          lowerUrl.includes('/movie/') ||
          lowerUrl.includes('/series/');

        // Check if stream is explicitly a LIVE stream or linear channel
        const is24hOrLinearLive =
          !isVodOrSeries &&
          (lowerGroup.includes('24h') ||
            lowerGroup.includes('24 horas') ||
            lowerGroup.includes('24 hora') ||
            lowerGroup.includes('canal') ||
            lowerGroup.includes('canais') ||
            lowerGroup.includes('ao vivo') ||
            lowerGroup.includes('live') ||
            lowerGroup.includes('tv aberta') ||
            lowerGroup.includes('abertos') ||
            lowerGroup.includes('esporte') ||
            lowerGroup.includes('noticia') ||
            lowerGroup.includes('variedades') ||
            lowerGroup.includes('premiere') ||
            lowerName.startsWith('24h') ||
            lowerName.includes(' 24h') ||
            lowerName.includes('24 horas') ||
            lowerUrl.includes('/live/') ||
            lowerUrl.includes('output=ts'));

        // Check if stream is an on-demand Series episode
        const hasEpisodePattern =
          /\b[sS]\d+\s*[eE]\d+\b/i.test(lowerName) ||
          /\b[tT]\d+\s*[eE]\d+\b/i.test(lowerName) ||
          /\btemporada\s*\d+/i.test(lowerName) ||
          /\bseason\s*\d+/i.test(lowerName) ||
          /\bep[ií]s[oó]dio\s*\d+/i.test(lowerName);

        const isVodSeriesGroup =
          (lowerGroup.includes('serie') ||
            lowerGroup.includes('série') ||
            lowerGroup.includes('seriados') ||
            lowerGroup.includes('anime') ||
            lowerGroup.includes('novela') ||
            lowerGroup.includes('dorama')) &&
          !lowerGroup.includes('filme') &&
          !is24hOrLinearLive;

        const isSeriesMatch =
          !is24hOrLinearLive &&
          (lowerUrl.includes('/series/') ||
            (hasEpisodePattern && !lowerUrl.includes('/live/')) ||
            isVodSeriesGroup);

        // Check if stream is an on-demand VOD Movie
        const isVodMovieGroup =
          (lowerGroup.includes('[filmes]') ||
            lowerGroup.includes('[vod]') ||
            lowerGroup.includes('filmes |') ||
            lowerGroup.includes('filme |') ||
            lowerGroup.includes('movies |') ||
            lowerGroup.includes('movie |') ||
            lowerGroup.includes('vod |') ||
            lowerGroup.includes('vod filmes') ||
            lowerGroup.includes('cinema vod') ||
            lowerGroup.includes('lancamentos') ||
            lowerGroup.includes('lançamentos') ||
            lowerGroup.includes('on demand filmes')) &&
          !is24hOrLinearLive;

        const isVodVideoFile =
          (lowerUrl.endsWith('.mp4') ||
            lowerUrl.endsWith('.mkv') ||
            lowerUrl.endsWith('.avi') ||
            lowerUrl.endsWith('.webm')) &&
          !lowerUrl.includes('/live/') &&
          !is24hOrLinearLive;

        const isMovieMatch =
          !isSeriesMatch &&
          !is24hOrLinearLive &&
          (lowerUrl.includes('/movie/') || isVodMovieGroup || isVodVideoFile);

        // Routing based on classification
        if (isSeriesMatch) {
          const { cleanSeriesTitle, seasonNumber, episodeNumber, episodeTitle } = extractSeriesEpisodeInfo(rawName);
          const seriesKey = cleanSeriesTitle.toLowerCase();
          const existing = seriesMap.get(seriesKey);

          const epStreamUrl = streamUrl;
          const baseUrlNoExt = epStreamUrl.replace(/\.(mp4|mkv|ts|avi)$/i, '');
          const backupStreams = [
            epStreamUrl,
            baseUrlNoExt + '.m3u8',
            baseUrlNoExt + '.mp4',
            baseUrlNoExt + '.mkv',
            baseUrlNoExt + '.ts',
            baseUrlNoExt
          ];

          if (existing) {
            existing.episodes.push({
              id: `ep-${existing.id}-s${seasonNumber}-e${episodeNumber}-${existing.episodes.length + 1}`,
              seasonNumber,
              episodeNumber,
              title: episodeTitle || `Episódio ${episodeNumber}`,
              synopsis: `Temporada ${seasonNumber} • Episódio ${episodeNumber} de ${cleanSeriesTitle}`,
              duration: 45,
              thumbnail: currentMetadata.logo || existing.poster,
              streamUrl: epStreamUrl,
              backupStreams,
            });
            if (seasonNumber > existing.seasonsCount) {
              existing.seasonsCount = seasonNumber;
            }
          } else {
            const newSeriesId = `ser-${seriesMap.size + 1}`;
            const resolvedPoster = currentMetadata.logo || generateDynamicPoster(cleanSeriesTitle, rawGroup);
            const newSeries: Series = {
              id: newSeriesId,
              title: cleanSeriesTitle,
              poster: resolvedPoster,
              backdrop: currentMetadata.logo || resolvedPoster,
              category: rawGroup,
              year: new Date().getFullYear(),
              seasonsCount: Math.max(1, seasonNumber),
              rating: isAdult ? '18' : '14',
              score: 8.5,
              synopsis: `Série ${cleanSeriesTitle} com temporadas e episódios organizados.`,
              cast: ['Elenco Principal'],
              isAdult,
              episodes: [
                {
                  id: `ep-${newSeriesId}-s${seasonNumber}-e${episodeNumber}-1`,
                  seasonNumber,
                  episodeNumber,
                  title: episodeTitle || `Episódio ${episodeNumber}`,
                  synopsis: `Temporada ${seasonNumber} • Episódio ${episodeNumber} de ${cleanSeriesTitle}`,
                  duration: 45,
                  thumbnail: resolvedPoster,
                  streamUrl: epStreamUrl,
                  backupStreams,
                },
              ],
            };
            seriesMap.set(seriesKey, newSeries);
          }
        } else if (isMovieMatch) {
          const releaseYear = extractReleaseYear(rawName, currentMetadata.name);

          movies.push({
            id: `mov-${movies.length + 1}`,
            title: rawName || `Filme ${movies.length + 1}`,
            poster: currentMetadata.logo || defaultMoviePoster,
            backdrop: currentMetadata.logo || defaultMovieBackdrop,
            streamUrl,
            backupStreams: [
              streamUrl,
              streamUrl.replace(/\.(mp4|mkv|ts|avi)$/i, '') + '.mkv',
              streamUrl.replace(/\.(mp4|mkv|ts|avi)$/i, '') + '.mp4',
              streamUrl.replace(/\.(mp4|mkv|ts|avi)$/i, '') + '.ts',
              streamUrl.replace(/\.(mp4|mkv|ts|avi)$/i, ''),
            ],
            category: rawGroup,
            year: releaseYear,
            duration: 110,
            rating: isAdult ? '18' : '14',
            score: 8.2,
            synopsis: `Filme ${rawName} adicionado ao catálogo VOD sob a categoria "${rawGroup}".`,
            cast: ['Elenco'],
            director: 'Diretor',
            quality:
              currentMetadata.resolution === '4K'
                ? '4K'
                : currentMetadata.resolution === 'FHD'
                ? '1080p'
                : '720p',
            isAdult,
          });
        } else {
          // Live TV Channel
          const channelName = rawName || `Canal ${channelIndex}`;
          const resolvedLogo = resolveCorrectChannelLogo(
            channelName,
            currentMetadata.logo || defaultChannelLogo,
            currentMetadata.tvgId
          );

          // Build backup streams for maximum playback reliability
          const backupStreams: string[] = [];
          if (streamUrl.endsWith('.ts')) {
            backupStreams.push(streamUrl);
            backupStreams.push(streamUrl.slice(0, -3) + '.m3u8');
          } else if (streamUrl.endsWith('.m3u8')) {
            backupStreams.push(streamUrl);
            backupStreams.push(streamUrl.slice(0, -5) + '.ts');
          } else {
            backupStreams.push(streamUrl);
            backupStreams.push(`${streamUrl}.m3u8`);
            backupStreams.push(`${streamUrl}.ts`);
          }

          channels.push({
            id: `ch-m3u-${channelIndex}`,
            num: channelIndex,
            name: channelName,
            logo: resolvedLogo,
            streamUrl,
            backupStreams,
            groupTitle: rawGroup,
            epgId: currentMetadata.tvgId,
            isAdult,
            rating: isAdult ? '18' : '12',
            resolution: currentMetadata.resolution,
          });
        }

        channelIndex++;
        currentMetadata = null;
      }
    }
  }

  const series = Array.from(seriesMap.values());
  return { channels, movies, series, errors };
}

/**
 * Helper to fetch content through the proxy first, with direct fetch as fallback.
 * Includes timeout control to prevent hanging requests.
 */
async function fetchViaProxyOrDirect(
  targetUrl: string,
  responseType: 'text' | 'json' = 'text',
  timeoutMs = 15000
): Promise<any> {
  const proxyPath = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1')
    ? '/api/proxy.php'
    : '/api/proxy';
  const proxyUrl = `${proxyPath}?url=${encodeURIComponent(targetUrl)}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // 1. Try proxy
  try {
    const res = await fetch(proxyUrl, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      return responseType === 'json' ? await res.json() : await res.text();
    }
  } catch (err) {
    clearTimeout(timeoutId);
  }

  // 2. Try direct fetch (apenas se seguro; no browser em HTTPS, requisição direta HTTP causa erro imediato de Mixed Content)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && !targetUrl.startsWith('https://')) {
    throw new Error(`Proxy indisponível para conexão com ${targetUrl}`);
  }

  const directController = new AbortController();
  const directTimeoutId = setTimeout(() => directController.abort(), timeoutMs);
  try {
    const directRes = await fetch(targetUrl, { signal: directController.signal });
    clearTimeout(directTimeoutId);
    if (!directRes.ok) {
      throw new Error(`HTTP ${directRes.status}`);
    }
    return responseType === 'json' ? await directRes.json() : await directRes.text();
  } catch (err: any) {
    clearTimeout(directTimeoutId);
    throw err;
  }
}

/**
 * Robust Xtream Codes integration supporting both official JSON API and M3U endpoints.
 */
export async function testAndFetchXtream(server: string, user: string, pass: string): Promise<ParseResult> {
  let cleanServer = server.trim().replace(/\/+$/, '');
  if (!cleanServer.startsWith('http://') && !cleanServer.startsWith('https://')) {
    cleanServer = `http://${cleanServer}`;
  }

  const defaultMoviePoster = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500&auto=format&fit=crop&q=80';
  const defaultMovieBackdrop = 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1200&auto=format&fit=crop&q=80';
  const defaultChannelLogo = 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=128&auto=format&fit=crop&q=80';

  // ==========================================
  // PHASE 1: Official Xtream Codes JSON API
  // ==========================================
  try {
    const authUrl = `${cleanServer}/player_api.php?username=${encodeURIComponent(user)}&password=${encodeURIComponent(pass)}`;
    const authData = await fetchViaProxyOrDirect(authUrl, 'json', 8000);

    if (authData && authData.user_info && (authData.user_info.auth === 1 || authData.user_info.status === 'Active')) {
      // Prioritize live streams first for fast loading, while fetching VOD and Series in parallel.
      // Timeouts de VOD/séries aumentados (15s -> 60s): catálogos grandes (10k+ filmes) fazem o
      // painel Xtream demorar bem mais pra gerar essa resposta de uma vez só — 15s era curto
      // demais e cortava a busca antes do painel terminar, deixando filmes/séries sem atualizar
      // silenciosamente mesmo com canais funcionando normalmente (canais têm catálogo bem menor).
      const [liveCatsData, vodCatsData, seriesCatsData, liveStreams, vodStreams, seriesStreams] = await Promise.allSettled([
        fetchViaProxyOrDirect(`${authUrl}&action=get_live_categories`, 'json', 8000),
        fetchViaProxyOrDirect(`${authUrl}&action=get_vod_categories`, 'json', 20000),
        fetchViaProxyOrDirect(`${authUrl}&action=get_series_categories`, 'json', 20000),
        fetchViaProxyOrDirect(`${authUrl}&action=get_live_streams`, 'json', 12000),
        fetchViaProxyOrDirect(`${authUrl}&action=get_vod_streams`, 'json', 60000),
        fetchViaProxyOrDirect(`${authUrl}&action=get_series`, 'json', 60000),
      ]);

      // Deixa de falhar em silêncio: se a busca de filmes/séries falhar (timeout ou erro), registra
      // pra ficar rastreável e visível pro admin — antes, um catálogo grande simplesmente não
      // atualizava sem nenhum indício do motivo.
      const fetchWarnings: string[] = [];
      if (vodStreams.status === 'rejected') {
        console.warn('[Xtream] Falha ao buscar get_vod_streams (filmes não serão atualizados nesta tentativa):', vodStreams.reason);
        fetchWarnings.push('Filmes não foram atualizados: o painel demorou demais para responder (catálogo muito grande). Tente novamente.');
      }
      if (seriesStreams.status === 'rejected') {
        console.warn('[Xtream] Falha ao buscar get_series (séries não serão atualizadas nesta tentativa):', seriesStreams.reason);
        fetchWarnings.push('Séries não foram atualizadas: o painel demorou demais para responder (catálogo muito grande). Tente novamente.');
      }

      const liveCatMap = new Map<string, string>();
      if (liveCatsData.status === 'fulfilled' && Array.isArray(liveCatsData.value)) {
        liveCatsData.value.forEach((c: any) => {
          if (c.category_id && c.category_name) liveCatMap.set(String(c.category_id), c.category_name);
        });
      }

      const vodCatMap = new Map<string, string>();
      if (vodCatsData.status === 'fulfilled' && Array.isArray(vodCatsData.value)) {
        vodCatsData.value.forEach((c: any) => {
          if (c.category_id && c.category_name) vodCatMap.set(String(c.category_id), c.category_name);
        });
      }

      const seriesCatMap = new Map<string, string>();
      if (seriesCatsData.status === 'fulfilled' && Array.isArray(seriesCatsData.value)) {
        seriesCatsData.value.forEach((c: any) => {
          if (c.category_id && c.category_name) seriesCatMap.set(String(c.category_id), c.category_name);
        });
      }

      const channels: Channel[] = [];
      const movies: Movie[] = [];
      const series: Series[] = [];

      // Parse Live Channels
      if (liveStreams.status === 'fulfilled' && Array.isArray(liveStreams.value)) {
        liveStreams.value.forEach((stream: any, index: number) => {
          const streamId = stream.stream_id || stream.id;
          if (!streamId) return;

          const rawGroup = (stream.category_id && liveCatMap.get(String(stream.category_id))) || stream.category_name || 'Ao Vivo';
          const streamName = stream.name || `Canal ${index + 1}`;
          const isAdult =
            rawGroup.toLowerCase().includes('adult') ||
            rawGroup.toLowerCase().includes('18+') ||
            rawGroup.toLowerCase().includes('xxx') ||
            streamName.toLowerCase().includes('xxx');

          // Web-optimized stream URL addressing:
          // In web browsers, .m3u8 (HLS) is the gold standard. We also supply .ts and alternative paths in backupStreams.
          const primaryStreamUrl = `${cleanServer}/live/${user}/${pass}/${streamId}.m3u8`;
          const backupStreams = [
            `${cleanServer}/live/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${streamId}.m3u8`,
            `${cleanServer}/live/${user}/${pass}/${streamId}.ts`,
            `${cleanServer}/live/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${streamId}.ts`,
            `${cleanServer}/${user}/${pass}/${streamId}.m3u8`,
            `${cleanServer}/${user}/${pass}/${streamId}`,
            `${cleanServer}/live/${user}/${pass}/${streamId}`,
            `${cleanServer}/hls/${user}/${pass}/${streamId}.m3u8`,
          ];

          const resolvedLogo = resolveCorrectChannelLogo(
            streamName,
            stream.stream_icon || defaultChannelLogo,
            stream.epg_channel_id
          );

          channels.push({
            id: `ch-xtream-${streamId}`,
            num: stream.num || index + 1,
            name: streamName,
            logo: resolvedLogo,
            streamUrl: primaryStreamUrl,
            backupStreams,
            groupTitle: rawGroup,
            epgId: stream.epg_channel_id || undefined,
            isAdult,
            resolution: (() => {
              const { detectedResolution } = extractChannelQualityInfo(streamName, rawGroup);
              return detectedResolution || 'HD';
            })(),
          });
        });
      }

      // Parse VOD Movies
      if (vodStreams.status === 'fulfilled' && Array.isArray(vodStreams.value)) {
        vodStreams.value.forEach((movie: any, index: number) => {
          const streamId = movie.stream_id || movie.id;
          if (!streamId) return;

          const rawGroup = (movie.category_id && vodCatMap.get(String(movie.category_id))) || movie.category_name || 'Filmes';
          const title = movie.name || `Filme ${index + 1}`;
          const ext = movie.container_extension || 'mp4';
          const primaryStreamUrl = `${cleanServer}/movie/${user}/${pass}/${streamId}.mp4`;
          const backupStreams = [
            `${cleanServer}/movie/${user}/${pass}/${streamId}.mp4`,
            `${cleanServer}/movie/${user}/${pass}/${streamId}.${ext}`,
            `${cleanServer}/movie/${user}/${pass}/${streamId}.m3u8`,
            `${cleanServer}/movie/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${streamId}.mp4`,
            `${cleanServer}/movie/${user}/${pass}/${streamId}.mkv`,
            `${cleanServer}/movie/${user}/${pass}/${streamId}.ts`,
            `${cleanServer}/movie/${user}/${pass}/${streamId}`,
            `${cleanServer}/${user}/${pass}/${streamId}.${ext}`,
            `${cleanServer}/${user}/${pass}/${streamId}.mp4`,
            `${cleanServer}/${user}/${pass}/${streamId}.mkv`,
            `${cleanServer}/${user}/${pass}/${streamId}`,
          ];

          const isAdult =
            rawGroup.toLowerCase().includes('adult') ||
            rawGroup.toLowerCase().includes('18+') ||
            rawGroup.toLowerCase().includes('xxx');

          const resolvedMoviePoster =
            movie.stream_icon ||
            movie.cover ||
            movie.cover_big ||
            movie.movie_image ||
            movie.poster ||
            (movie.backdrop_path && movie.backdrop_path.length > 0 ? movie.backdrop_path[0] : '') ||
            generateDynamicPoster(title, rawGroup, movie.year);

          movies.push({
            id: `mov-xtream-${streamId}`,
            title,
            poster: resolvedMoviePoster,
            backdrop: movie.backdrop_path && movie.backdrop_path.length > 0 ? movie.backdrop_path[0] : (movie.stream_icon || defaultMovieBackdrop),
            streamUrl: primaryStreamUrl,
            backupStreams,
            category: rawGroup,
            year: extractReleaseYear(title, movie.year, movie.releaseDate, movie.release_date, movie.added_date),
            duration: movie.duration_secs ? Math.round(movie.duration_secs / 60) : 110,
            rating: isAdult ? '18' : '14',
            score: movie.rating ? parseFloat(movie.rating) : 8.0,
            synopsis: movie.plot || `Filme ${title} importado do servidor Xtream.`,
            cast: movie.cast ? [movie.cast] : ['Elenco'],
            director: movie.director || 'Diretor',
            quality: title.toUpperCase().includes('4K') ? '4K' : '1080p',
            isAdult,
          });
        });
      }

      // Parse Series
      if (seriesStreams.status === 'fulfilled' && Array.isArray(seriesStreams.value)) {
        const xtreamSeriesMap = new Map<string, Series>();
        seriesStreams.value.forEach((ser: any, index: number) => {
          const seriesId = ser.series_id || ser.id;
          if (!seriesId) return;

          const rawGroup = (ser.category_id && seriesCatMap.get(String(ser.category_id))) || ser.category_name || 'Séries';
          const rawTitle = ser.name || `Série ${index + 1}`;
          const lowerGroup = rawGroup.toLowerCase();
          const lowerTitle = rawTitle.toLowerCase();

          // 1. Filtrar Canais 24 Horas que o provedor colocou na categoria de Séries
          const is24hChannel =
            lowerGroup.includes('24h') ||
            lowerGroup.includes('24 horas') ||
            lowerGroup.includes('24hs') ||
            lowerGroup.includes('24/7') ||
            lowerGroup.includes('canais 24') ||
            lowerTitle.startsWith('24h') ||
            lowerTitle.includes(' 24h') ||
            lowerTitle.includes('24 horas');

          if (is24hChannel) {
            channels.push({
              id: `ch-xtream-24h-${seriesId}`,
              name: rawTitle,
              num: channels.length + 1,
              logo: ser.cover || ser.stream_icon || '',
              groupTitle: rawGroup.replace(/^\[?SÉRIES\]?\s*[\-:|]?\s*/i, 'Canais 24 Horas | '),
              streamUrl: `${cleanServer}/live/${user}/${pass}/${seriesId}.m3u8`,
              backupStreams: [
                `${cleanServer}/live/${user}/${pass}/${seriesId}.ts`,
                `${cleanServer}/series/${user}/${pass}/${seriesId}.mp4`,
              ],
              resolution: 'HD',
              isAdult: lowerGroup.includes('adult') || lowerGroup.includes('18+') || lowerGroup.includes('xxx'),
            });
            return;
          }
          
          const { cleanSeriesTitle, seasonNumber } = extractSeriesEpisodeInfo(rawTitle);
          // Agrupar por nome limpo + categoria para não misturar séries de categorias diferentes
          const seriesKey = `${cleanSeriesTitle.toLowerCase().trim()}_${rawGroup.toLowerCase().trim()}`;

          const isAdult =
            lowerGroup.includes('adult') ||
            lowerGroup.includes('18+') ||
            lowerGroup.includes('xxx');

          const primaryStreamUrl = `${cleanServer}/series/${user}/${pass}/${seriesId}.mp4`;
          const backupStreams = [
            `${cleanServer}/series/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${seriesId}.mp4`,
            `${cleanServer}/series/${user}/${pass}/${seriesId}.mkv`,
          ];

          const resolvedCover =
            ser.cover ||
            ser.stream_icon ||
            ser.poster ||
            (ser.backdrop_path && ser.backdrop_path.length > 0 ? ser.backdrop_path[0] : '') ||
            ser.movie_image ||
            generateDynamicPoster(cleanSeriesTitle, rawGroup, ser.releaseDate ? parseInt(ser.releaseDate.substring(0, 4), 10) : undefined);

          const resolvedBackdrop =
            (ser.backdrop_path && ser.backdrop_path.length > 0 ? ser.backdrop_path[0] : '') ||
            ser.cover ||
            ser.stream_icon ||
            ser.poster ||
            resolvedCover;

          const existing = xtreamSeriesMap.get(seriesKey);
          if (existing) {
            existing.seasonsCount += 1;
            if (!existing.xtreamIds) {
              const prevId = existing.id.replace('ser-xtream-', '');
              existing.xtreamIds = [{ id: prevId, season: 1 }];
            }
            if (!existing.xtreamIds.some(x => x.id === seriesId)) {
              existing.xtreamIds.push({ id: seriesId, season: seasonNumber });
            }
            if (ser.plot && (!existing.synopsis || existing.synopsis.includes('importada do servidor'))) {
              existing.synopsis = ser.plot;
            }
            if (ser.cover && (!existing.poster || existing.poster.includes('data:image'))) {
              existing.poster = resolvedCover;
            }
            const lm = providerTimestampToMs(ser.last_modified);
            if (lm && (!existing.lastModified || lm > existing.lastModified)) existing.lastModified = lm;
          } else {
            const newSeries: Series = {
              id: `ser-xtream-${seriesId}`,
              title: cleanSeriesTitle,
              lastModified: providerTimestampToMs(ser.last_modified), // detecta temporada/episódio novo
              poster: resolvedCover,
              backdrop: resolvedBackdrop,
              category: rawGroup,
              year: extractReleaseYear(cleanSeriesTitle, ser.releaseDate, ser.release_date, ser.year, ser.last_modified),
              seasonsCount: 1,
              rating: isAdult ? '18' : '14',
              score: ser.rating ? parseFloat(ser.rating) : 8.5,
              synopsis: ser.plot || `Série ${cleanSeriesTitle} em alta definição disponível no catálogo.`,
              cast: ser.cast ? (Array.isArray(ser.cast) ? ser.cast : [ser.cast]) : ['Elenco Principal'],
              director: ser.director || 'Diretor',
              isAdult,
              xtreamIds: [{ id: seriesId, season: seasonNumber }],
              episodes: [
                {
                  id: `ep-xtream-${seriesId}-dummy`,
                  seasonNumber: 1,
                  episodeNumber: 1,
                  title: `Carregar Episódios`,
                  synopsis: `Carregue os episódios para assistir.`,
                  duration: 45,
                  thumbnail: resolvedCover,
                  streamUrl: primaryStreamUrl,
                  backupStreams,
                },
              ],
            };
            xtreamSeriesMap.set(seriesKey, newSeries);
          }
        });
        
        series.push(...Array.from(xtreamSeriesMap.values()));
      }

      if (channels.length > 0 || movies.length > 0 || series.length > 0) {
        return {
          channels,
          movies,
          series,
          errors: fetchWarnings,
        };
      }
    }
  } catch (err: any) {
    console.warn('Xtream JSON API attempt failed, trying M3U playlist format...', err);
  }

  // ==========================================
  // PHASE 2: Xtream M3U Endpoints (get.php)
  // Tentativa rápida com timeout reduzido de 5s para não travar a aplicação
  // ==========================================
  const candidateUrls = [
    `${cleanServer}/get.php?username=${encodeURIComponent(user)}&password=${encodeURIComponent(pass)}&type=m3u_plus&output=m3u8`,
    `${cleanServer}/get.php?username=${encodeURIComponent(user)}&password=${encodeURIComponent(pass)}&type=m3u_plus`,
    `${cleanServer}/get.php?username=${encodeURIComponent(user)}&password=${encodeURIComponent(pass)}`,
  ];

  for (const url of candidateUrls) {
    try {
      const text = await fetchViaProxyOrDirect(url, 'text', 5000);
      if (text && (text.includes('#EXTM3U') || text.includes('#EXTINF'))) {
        const parsed = await parseM3U(text);
        if (parsed.channels.length > 0 || parsed.movies.length > 0 || parsed.series.length > 0) {
          return parsed;
        }
      }
    } catch {
      // Falha rápida na URL candidata
    }
  }

  // ==========================================
  // PHASE 3: Clear Error (NO FAKE 4 CHANNELS)
  // ==========================================
  throw new Error(
    `Não foi possível obter a lista do servidor Xtream Codes (${cleanServer}). Verifique se o endereço, porta, usuário e senha estão corretos e se o servidor está ativo.`
  );
}

/**
 * Intelligent Multi-DNS fallback for Xtream Codes.
 * Tenta conectar sequencialmente em cada DNS da lista até encontrar uma operacional.
 */
export async function testAndFetchXtreamWithFallback(
  serverList: string[],
  user: string,
  pass: string,
  onProgress?: (msg: string) => void
): Promise<ParseResult & { usedServer: string }> {
  // V8.1.9: a ordem vem de quem chama (Gerenciador primeiro). Nenhum servidor fixo é adicionado aqui.
  const rawList = [...serverList];

  const cleanList = Array.from(
    new Set(
      rawList
        .map((s) => (s || '').trim())
        .filter((s) => s.length > 0)
        .map((s) => (s.startsWith('http://') || s.startsWith('https://') ? s : `http://${s}`))
    )
  );

  if (cleanList.length === 0) {
    throw new Error('Nenhuma DNS ou URL de servidor fornecida.');
  }

  let lastError: any = null;

  for (let i = 0; i < cleanList.length; i++) {
    const currentServer = cleanList[i];
    try {
      if (onProgress) {
        onProgress(`Conectando na DNS ${i + 1}/${cleanList.length}: ${currentServer}...`);
      }
      const result = await testAndFetchXtream(currentServer, user, pass);
      if (result.channels.length > 0 || result.movies.length > 0 || result.series.length > 0) {
        if (onProgress) {
          onProgress(`Conectado com sucesso na DNS ${currentServer}!`);
        }
        return { ...result, usedServer: currentServer };
      }
    } catch (err: any) {
      console.warn(`[Multi-DNS Fallback] Falha na DNS ${currentServer}:`, err?.message || err);
      lastError = err;
    }
  }

  throw new Error(
    `Todas as ${cleanList.length} DNSs cadastradas falharam. Último erro: ${lastError?.message || 'Servidor indisponível ou credenciais inválidas.'}`
  );
}

/**
 * Fetches full seasons and chapters for an Xtream series on-demand.
 */
export async function fetchXtreamSeriesInfo(
  server: string | string[],
  user: string,
  pass: string,
  seriesId: string | number
): Promise<{
  seasons: any[];
  episodes: any[];
  info?: any;
  seasonsCount: number;
} | null> {
  const rawServers = Array.isArray(server) ? server : [server];
  const candidateServers = Array.from(
    new Set(
      rawServers
        .filter(Boolean)
        .map((s) => s.trim().replace(/\/+$/, ''))
        .map((s) => (s.startsWith('http://') || s.startsWith('https://') ? s : `http://${s}`))
        // V9.0.2: só a DNS que veio (a da lista) — sem a lista antiga de várias DNS
    )
  );

  const rawId = String(seriesId).replace('ser-xtream-', '').replace('ser-', '');

  for (const cleanServer of candidateServers) {
    const url = `${cleanServer}/player_api.php?username=${encodeURIComponent(user)}&password=${encodeURIComponent(pass)}&action=get_series_info&series_id=${rawId}`;

    try {
      const data = await fetchViaProxyOrDirect(url, 'json', 8000);
      if (!data || (!data.episodes && !data.seasons && !data.info)) {
        continue;
      }

    const episodes: any[] = [];
    const seasonsList = Array.isArray(data.seasons)
      ? data.seasons
      : data.seasons && typeof data.seasons === 'object'
      ? Object.values(data.seasons)
      : [];

    let maxSeason = 1;
    const rawEpisodes = data.episodes;

    if (Array.isArray(rawEpisodes)) {
      // Direct array of episode objects
      rawEpisodes.forEach((ep: any, idx: number) => {
        if (!ep || typeof ep !== 'object') return;
        let seasonNum =
          parseInt(ep.season || ep.season_num || ep.seasonNumber || 1, 10) || 1;
        
        let epNum =
          parseInt(
            ep.episode_num || ep.episode || ep.episodeNumber || ep.num || idx + 1,
            10
          ) || idx + 1;

        const originalSeasonNum = seasonNum;
        const originalEpNum = epNum;

        const rawTitle = ep.title || ep.name || `Episódio ${epNum}`;
        const explicitMatch = rawTitle.match(/(?:[\[\(\s\-_]|^)(?:s|t)(\d{1,2})[\s\.\-_]*(?:e|ep)(\d{1,3})(?:[\]\)\s\-_]|$)/i) || 
                              rawTitle.match(/(?:[\[\(\s\-_]|^)(\d{1,2})\s*[xX]\s*(\d{1,3})(?:[\]\)\s\-_]|$)/);
        if (explicitMatch) {
            seasonNum = parseInt(explicitMatch[1], 10);
            epNum = parseInt(explicitMatch[2], 10);
        }

        if (seasonNum > maxSeason) maxSeason = seasonNum;

        const epId = ep.id || ep.stream_id || `${rawId}-${originalSeasonNum}-${originalEpNum}`;
        const ext = ep.container_extension ? String(ep.container_extension).trim() : (ep.extension ? String(ep.extension).trim() : 'mp4');

        const primaryStreamUrl = `${cleanServer}/series/${user}/${pass}/${epId}.mp4`;
        const backupStreams = [
            `${cleanServer}/series/${user}/${pass}/${epId}.mp4`,
            `${cleanServer}/series/${user}/${pass}/${epId}.m3u8`,
            `${cleanServer}/series/${user}/${pass}/${epId}.${ext}`,
            `${cleanServer}/series/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${epId}.mp4`,
            `${cleanServer}/series/${user}/${pass}/${epId}.mkv`,
            `${cleanServer}/series/${user}/${pass}/${epId}.ts`,
            `${cleanServer}/series/${user}/${pass}/${epId}`,
            `${cleanServer}/${user}/${pass}/${epId}.mp4`,
            `${cleanServer}/${user}/${pass}/${epId}.${ext}`,
            `${cleanServer}/${user}/${pass}/${epId}.mkv`,
            `${cleanServer}/${user}/${pass}/${epId}`,
          ];

        episodes.push({
          id: `ep-xtream-${rawId}-s${seasonNum}-e${epNum}-${epId}`,
          seasonNumber: seasonNum,
          episodeNumber: epNum,
          title: rawTitle,
          synopsis:
            ep.info?.plot ||
            ep.info?.overview ||
            ep.plot ||
            ep.overview ||
            `Temporada ${seasonNum} • Episódio ${epNum}`,
          duration: ep.info?.duration_secs
            ? Math.round(ep.info.duration_secs / 60)
            : ep.info?.duration
            ? parseInt(ep.info.duration, 10) || 45
            : 45,
          thumbnail:
            ep.info?.movie_image ||
            ep.info?.cover ||
            ep.movie_image ||
            data.info?.cover ||
            '',
          streamUrl: primaryStreamUrl,
          backupStreams,
        });
      });
    } else if (rawEpisodes && typeof rawEpisodes === 'object') {
      // Key-value pairs where key is season number/name and value is list or object
      Object.entries(rawEpisodes).forEach(([seasonKey, epListOrObj]) => {
        const numMatch = seasonKey.match(/\d+/);
        const parsedKeySeason = numMatch ? parseInt(numMatch[0], 10) : 1;
        const seasonNum = parsedKeySeason || 1;
        if (seasonNum > maxSeason) maxSeason = seasonNum;

        const epList = Array.isArray(epListOrObj) ? epListOrObj : [epListOrObj];

        epList.forEach((ep: any, idx: number) => {
          if (!ep || typeof ep !== 'object') return;
          let actualSeason =
            parseInt(
              ep.season || ep.season_num || ep.seasonNumber || seasonNum,
              10
            ) || seasonNum;
          
          let epNum =
            parseInt(
              ep.episode_num || ep.episode || ep.episodeNumber || ep.num || idx + 1,
              10
            ) || idx + 1;

          const originalActualSeason = actualSeason;
          const originalEpNum = epNum;

          const rawTitle = ep.title || ep.name || `Episódio ${epNum}`;
          const explicitMatch = rawTitle.match(/(?:[\[\(\s\-_]|^)(?:s|t)(\d{1,2})[\s\.\-_]*(?:e|ep)(\d{1,3})(?:[\]\)\s\-_]|$)/i) || 
                                rawTitle.match(/(?:[\[\(\s\-_]|^)(\d{1,2})\s*[xX]\s*(\d{1,3})(?:[\]\)\s\-_]|$)/);
          if (explicitMatch) {
              actualSeason = parseInt(explicitMatch[1], 10);
              epNum = parseInt(explicitMatch[2], 10);
          }

          if (actualSeason > maxSeason) maxSeason = actualSeason;

          const epId = ep.id || ep.stream_id || `${rawId}-${originalActualSeason}-${originalEpNum}`;
          const ext = ep.container_extension ? String(ep.container_extension).trim() : (ep.extension ? String(ep.extension).trim() : 'mp4');

          const primaryStreamUrl = `${cleanServer}/series/${user}/${pass}/${epId}.mp4`;
          const backupStreams = [
            `${cleanServer}/series/${user}/${pass}/${epId}.mp4`,
            `${cleanServer}/series/${user}/${pass}/${epId}.m3u8`,
            `${cleanServer}/series/${user}/${pass}/${epId}.${ext}`,
            `${cleanServer}/series/${encodeURIComponent(user)}/${encodeURIComponent(pass)}/${epId}.mp4`,
            `${cleanServer}/series/${user}/${pass}/${epId}.mkv`,
            `${cleanServer}/series/${user}/${pass}/${epId}.ts`,
            `${cleanServer}/series/${user}/${pass}/${epId}`,
            `${cleanServer}/${user}/${pass}/${epId}.mp4`,
            `${cleanServer}/${user}/${pass}/${epId}.${ext}`,
            `${cleanServer}/${user}/${pass}/${epId}.mkv`,
            `${cleanServer}/${user}/${pass}/${epId}`,
          ];

          episodes.push({
            id: `ep-xtream-${rawId}-s${actualSeason}-e${epNum}-${epId}`,
            seasonNumber: actualSeason,
            episodeNumber: epNum,
            title: rawTitle,
            synopsis:
              ep.info?.plot ||
              ep.info?.overview ||
              ep.plot ||
              ep.overview ||
              `Temporada ${actualSeason} • Episódio ${epNum}`,
            duration: ep.info?.duration_secs
              ? Math.round(ep.info.duration_secs / 60)
              : ep.info?.duration
              ? parseInt(ep.info.duration, 10) || 45
              : 45,
            thumbnail:
              ep.info?.movie_image ||
              ep.info?.cover ||
              ep.movie_image ||
              data.info?.cover ||
              '',
            streamUrl: primaryStreamUrl,
            backupStreams,
          });
        });
      });
    }

    episodes.sort((a, b) => {
      if (a.seasonNumber !== b.seasonNumber) return a.seasonNumber - b.seasonNumber;
      return a.episodeNumber - b.episodeNumber;
    });

    return {
      seasons: seasonsList,
      episodes,
      info: data.info,
      seasonsCount: Math.max(1, maxSeason, seasonsList.length),
    };
  } catch (err) {
    console.warn(`[Xtream] Falha ao buscar get_series_info em ${cleanServer} para ${seriesId}:`, err);
  }
}
  return null;
}
